import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Result, Button, Spin, Typography, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { API } from '../components/Initilization';
import { useNotificationContext } from '../components/NotificationContext';

const { Text, Title } = Typography;

const PurchaseVerify = () => {
  const [verifying, setVerifying] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [refId, setRefId] = useState('');
  const [error, setError] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const notif = useNotificationContext();

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const authority = params.get('Authority');
      const status = params.get('Status');

      // Check if user cancelled payment
      if (!status || status !== 'OK') {
        setPaymentSuccess(false);
        setError(status === 'NOK' ? 'پرداخت توسط کاربر لغو شد' : 'پرداخت ناموفق بود');
        setVerifying(false);
        
        notif.open({
          type: 'error',
          style: { width: "450px" },
          className: "fYekan-bold",
          placement: 'topRight',
          message: 'خطا',
          description: 'پرداخت ناموفق بود یا توسط کاربر لغو شد',
        });
        return;
      }

      if (!authority) {
        setPaymentSuccess(false);
        setError('کد پیگیری پرداخت یافت نشد');
        setVerifying(false);
        return;
      }

      // Call the verify endpoint on your backend
      const formData = new FormData();
      formData.append('Authority', authority);

      const response = await API.post('/api/payment/verify', formData, {
        NeedsAuthorization: true
      });

      if (response.status === 200 && response.data.status) {
        setPaymentSuccess(true);
        setRefId(response.data.RefID);
        
        // Update purchase payment status if order exists
        const orderId = sessionStorage.getItem('currentOrderId');
        if (orderId) {
          await updatePurchaseStatus(orderId);
        }
        
        // Clear cart and storage
        localStorage.setItem('designedOrders', null);
        sessionStorage.removeItem('currentOrderId');
        sessionStorage.removeItem('paymentAuthority');
        
        notif.open({
          type: 'success',
          style: { width: "450px" },
          className: "fYekan-bold",
          placement: 'topRight',
          message: 'موفق',
          description: 'پرداخت با موفقیت انجام شد. سفارش شما ثبت شد.',
        });
      } else {
        setPaymentSuccess(false);
        setError(response.data.message || 'پرداخت ناموفق بود');
        
        notif.open({
          type: 'error',
          style: { width: "450px" },
          className: "fYekan-bold",
          placement: 'topRight',
          message: 'خطا',
          description: response.data.message || 'تایید پرداخت با خطا مواجه شد',
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setPaymentSuccess(false);
      setError('خطا در تایید پرداخت. لطفاً با پشتیبانی تماس بگیرید.');
      
      notif.open({
        type: 'error',
        style: { width: "450px" },
        className: "fYekan-bold",
        placement: 'topRight',
        message: 'خطای سیستمی',
        description: 'خطا در برقراری ارتباط با سرور',
      });
    } finally {
      setVerifying(false);
    }
  };

  const updatePurchaseStatus = async (orderId) => {
    try {
      const formData = new FormData();
      formData.append('PurchasePK', orderId);
      
      const response = await API.put('/api/order/pay-purchase', formData, {
        NeedsAuthorization: true
      });
      
      if (response.status === 200) {
        console.log('Purchase status updated successfully');
      }
    } catch (error) {
      console.error('Failed to update purchase status:', error);
      // Don't show error to user for this, as payment was successful
    }
  };

  if (verifying) {
    return (
      <Card style={{ maxWidth: '600px', margin: '50px auto', textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <Title level={4} className="fYekan-bold">در حال تایید پرداخت...</Title>
          <Text type="secondary" className="fYekan">
            لطفاً چند لحظه صبر کنید. در حال بررسی وضعیت پرداخت شما هستیم.
          </Text>
        </Space>
      </Card>
    );
  }

  return (
    <Card style={{ maxWidth: '800px', margin: '50px auto' }}>
      {paymentSuccess ? (
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 72 }} />}
          title={<span className="fYekan-bold">پرداخت موفق</span>}
          subTitle={
            <Space direction="vertical" className="fYekan">
              <Text>پرداخت شما با موفقیت انجام شد.</Text>
              {refId && (
                <Text strong>
                  شماره پیگیری: <Text code className="fEstedad">{refId}</Text>
                </Text>
              )}
              <Text type="secondary">
                سفارش شما ثبت و در حال پردازش می‌باشد.
              </Text>
            </Space>
          }
          extra={[
            <Button 
              type="primary" 
              key="orders" 
              onClick={() => navigate('/user-dashboard')}
              className="fYekan"
            >
              مشاهده سفارشات من
            </Button>,
            <Button 
              key="home" 
              onClick={() => navigate('/')}
              className="fYekan"
            >
              بازگشت به صفحه اصلی
            </Button>,
          ]}
        />
      ) : (
        <Result
          status="error"
          icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 72 }} />}
          title={<span className="fYekan-bold">پرداخت ناموفق</span>}
          subTitle={
            <Space direction="vertical" className="fYekan">
              <Text type="danger">{error}</Text>
              <Text type="secondary">
                در صورت کسر وجه، مبلغ تا ۷۲ ساعت آینده به حساب شما بازگردانده خواهد شد.
              </Text>
            </Space>
          }
          extra={[
            <Button 
              type="primary" 
              key="retry" 
              onClick={() => navigate('/cart')}
              className="fYekan"
            >
              تلاش مجدد
            </Button>,
            <Button 
              key="support" 
              onClick={() => navigate('/contact-us')}
              className="fYekan"
            >
              تماس با پشتیبانی
            </Button>,
            <Button 
              key="home" 
              onClick={() => navigate('/')}
              className="fYekan"
            >
              بازگشت به صفحه اصلی
            </Button>,
          ]}
        />
      )}
    </Card>
  );
};

export default PurchaseVerify;